<section class="cg-blog-editor-studio" [class.cg-blog-editor-studio--loading]="loading()">
  @if (loading()) {
    <div class="cg-blog-editor-studio__loading" aria-busy="true">
      <span class="cg-blog-editor-studio__spinner" aria-hidden="true"></span>
      <p>Loading editor…</p>
    </div>
  } @else {
    <header class="cg-blog-editor-studio__toolbar">
      <div class="cg-blog-editor-studio__toolbar-start">
        <a routerLink="/platform/blogs" class="cg-blog-editor-studio__back">
          <span aria-hidden="true">←</span> Back to list
        </a>
        <span class="cg-blog-editor-studio__toolbar-divider" aria-hidden="true"></span>
        <h1 class="cg-blog-editor-studio__doc-title">{{ displayTitle() }}</h1>
      </div>
      <div class="cg-blog-editor-studio__toolbar-end">
        @if (showSaveDraft()) {
          <button type="button" class="cg-blog-editor-studio__text-btn" (click)="saveDraft()" [disabled]="!canSaveDraft() || saving()">
            {{ saving() ? 'Saving…' : (isNewPost() ? 'Create draft' : 'Save draft') }}
          </button>
        }
        <button type="button" class="cg-blog-editor-studio__icon-btn" (click)="preview()" title="Preview" [disabled]="saving()">
          <span aria-hidden="true">⎘</span> Preview
        </button>
        <button
          type="button"
          class="cg-blog-editor-studio__icon-btn"
          (click)="toggleVersions()"
          title="Version history"
          [disabled]="isNewPost()"
          [class.cg-blog-editor-studio__icon-btn--active]="versionsOpen()"
        >
          <span aria-hidden="true">↺</span>
        </button>
        @if (showPublish() || showDuplicate() || showSchedule()) {
          <button
            type="button"
            class="cg-blog-editor-studio__icon-btn"
            (click)="toggleSettings()"
            title="More actions"
            [class.cg-blog-editor-studio__icon-btn--active]="settingsOpen()"
          >
            <span aria-hidden="true">⚙</span>
          </button>
        }
        @if (showPublish() && status === 'PUBLISHED') {
          <button type="button" class="cg-blog-editor-studio__publish-btn cg-blog-editor-studio__publish-btn--muted" (click)="unpublish()">
            Unpublish
          </button>
        } @else if (canPublish()) {
          <button type="button" class="cg-blog-editor-studio__publish-btn" (click)="publish()" [disabled]="saving()">
            {{ isNewPost() ? 'Create & publish' : 'Publish' }}
          </button>
        }
      </div>
    </header>

    @if (settingsOpen()) {
      <div class="cg-blog-editor-studio__flyout cg-blog-editor-studio__flyout--settings">
        @if (showPublish()) {
          <button type="button" (click)="validate()">Validate for publish</button>
        }
        @if (showDuplicate()) {
          <button type="button" (click)="duplicate()">Duplicate post</button>
        }
        @if (showSchedule() && status !== 'PUBLISHED' && scheduledAt) {
          <button type="button" (click)="schedule()">Confirm schedule</button>
        }
      </div>
    }

    @if (versionsOpen() && versions().length) {
      <div class="cg-blog-editor-studio__flyout cg-blog-editor-studio__flyout--versions">
        <p class="cg-blog-editor-studio__flyout-title">Version history</p>
        <ul>
          @for (v of versions(); track v._id) {
            <li>
              <span>v{{ v.versionNumber }} {{ v.label || '' }}</span>
              @if (showRestoreVersion()) {
                <button type="button" (click)="restoreVersion(v._id)">Restore</button>
              }
            </li>
          }
        </ul>
      </div>
    }

    @if (validationErrors().length) {
      <div class="cg-blog-editor-studio__validation" role="alert">
        <strong>Fix before publishing:</strong>
        <ul>
          @for (e of validationErrors(); track e) {
            <li>{{ e }}</li>
          }
        </ul>
      </div>
    }

    <div class="cg-blog-editor-studio__body">
      <aside class="cg-blog-editor-studio__sidebar cg-blog-editor-studio__sidebar--left">
        <nav class="cg-blog-editor-studio__nav" aria-label="Editor sections">
          @for (item of navItems; track item.id) {
            <button
              type="button"
              class="cg-blog-editor-studio__nav-item"
              [class.cg-blog-editor-studio__nav-item--active]="activeSection() === item.id"
              (click)="setSection(item.id)"
            >
              <span class="cg-blog-editor-studio__nav-icon" aria-hidden="true">{{ item.icon }}</span>
              {{ item.label }}
            </button>
          }
        </nav>

        <div class="cg-blog-editor-studio__meta-block">
          <span class="cg-blog-editor-studio__label">Status</span>
          <span class="cg-blog-editor-studio__status-badge" [attr.data-status]="status">{{ statusLabel() }}</span>
        </div>

        <div class="cg-blog-editor-studio__meta-block">
          <label class="cg-blog-editor-studio__label" for="blog-slug">Slug</label>
          <div class="cg-blog-editor-studio__slug-field">
            <input
              id="blog-slug"
              class="cg-blog-editor-studio__input"
              [(ngModel)]="slug"
              (ngModelChange)="onFieldChange()"
              name="slug"
              placeholder="article-slug"
            />
            @if (slug) {
              <a class="cg-blog-editor-studio__slug-link" [href]="'/blog/' + slug" target="_blank" rel="noopener" title="Open public URL">↗</a>
            }
          </div>
        </div>

        <div class="cg-blog-editor-studio__meta-block">
          <span class="cg-blog-editor-studio__label">Countries</span>
          <div class="cg-blog-editor-studio__checklist cg-blog-editor-studio__checklist--scroll">
            @for (c of countryOptions(); track c._id) {
              <label class="cg-blog-editor-studio__check">
                <input
                  type="checkbox"
                  [checked]="countryIds.includes(c._id)"
                  (change)="toggleCountry(c._id, $any($event.target).checked)"
                />
                <span>{{ c.name }}</span>
              </label>
            }
          </div>
        </div>

        <div class="cg-blog-editor-studio__meta-block">
          <label class="cg-blog-editor-studio__label" for="blog-schedule">Schedule</label>
          <input
            id="blog-schedule"
            type="datetime-local"
            class="cg-blog-editor-studio__input"
            [(ngModel)]="scheduledAt"
            name="scheduledAt"
          />
        </div>

        <label class="cg-blog-editor-studio__check cg-blog-editor-studio__check--featured">
          <input type="checkbox" [(ngModel)]="isFeatured" (ngModelChange)="onFieldChange()" name="isFeatured" />
          <span>Featured on homepage</span>
        </label>

        @if (isNewPost()) {
          <p class="cg-blog-editor-studio__autosave">Not saved yet — use Create draft or Create &amp; publish.</p>
        } @else if (autosaveLabel()) {
          <p class="cg-blog-editor-studio__autosave">{{ autosaveLabel() }}</p>
        }
      </aside>

      <main class="cg-blog-editor-studio__canvas" id="blog-editor-section-content">
        <div class="cg-blog-editor-studio__fields">
          <div class="cg-blog-editor-studio__field-box">
            <input
              class="cg-blog-editor-studio__title-input"
              [(ngModel)]="title"
              (ngModelChange)="onFieldChange()"
              name="title"
              placeholder="Enter article title…"
              aria-label="Article title"
            />
          </div>

          <div class="cg-blog-editor-studio__field-box">
            <label class="cg-blog-editor-studio__field-box-label" for="blog-excerpt">Excerpt / Teaser</label>
            <textarea
              id="blog-excerpt"
              class="cg-blog-editor-studio__excerpt-input"
              [(ngModel)]="excerpt"
              (ngModelChange)="onFieldChange()"
              name="excerpt"
              rows="3"
              placeholder="Write a short summary that encourages users to read the full article…"
              aria-label="Article excerpt"
            ></textarea>
          </div>

          <div class="cg-blog-editor-studio__field-box cg-blog-editor-studio__field-box--content">
            <div class="cg-blog-editor-studio__rich-editor">
              <div class="cg-blog-editor-studio__editor-toolbar" role="toolbar" aria-label="Formatting">
                <div class="cg-blog-editor-studio__toolbar-group">
                  <button
                    type="button"
                    class="cg-blog-editor-studio__toolbar-btn"
                    title="Bold"
                    aria-label="Bold"
                    (mousedown)="preventToolbarFocusLoss($event)"
                    (click)="formatInline('bold')"
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    type="button"
                    class="cg-blog-editor-studio__toolbar-btn"
                    title="Italic"
                    aria-label="Italic"
                    (mousedown)="preventToolbarFocusLoss($event)"
                    (click)="formatInline('italic')"
                  >
                    <em>I</em>
                  </button>
                  <button
                    type="button"
                    class="cg-blog-editor-studio__toolbar-btn cg-blog-editor-studio__toolbar-btn--underline"
                    title="Underline"
                    aria-label="Underline"
                    (mousedown)="preventToolbarFocusLoss($event)"
                    (click)="formatInline('underline')"
                  >
                    <span>U</span>
                  </button>
                </div>

                <span class="cg-blog-editor-studio__toolbar-divider" aria-hidden="true"></span>

                <div class="cg-blog-editor-studio__toolbar-group">
                  <button
                    type="button"
                    class="cg-blog-editor-studio__toolbar-btn"
                    title="Bullet list"
                    aria-label="Bullet list"
                    (mousedown)="preventToolbarFocusLoss($event)"
                    (click)="insertBulletList()"
                  >
                    <span aria-hidden="true">•≡</span>
                  </button>
                  <button
                    type="button"
                    class="cg-blog-editor-studio__toolbar-btn"
                    title="Numbered list"
                    aria-label="Numbered list"
                    (mousedown)="preventToolbarFocusLoss($event)"
                    (click)="insertNumberedList()"
                  >
                    <span aria-hidden="true">1.</span>
                  </button>
                </div>

                <span class="cg-blog-editor-studio__toolbar-divider" aria-hidden="true"></span>

                <div class="cg-blog-editor-studio__toolbar-group">
                  <button
                    type="button"
                    class="cg-blog-editor-studio__toolbar-btn"
                    title="Insert image"
                    aria-label="Insert image"
                    (mousedown)="preventToolbarFocusLoss($event)"
                    (click)="openToolbarImagePicker()"
                  >
                    <span class="cg-blog-editor-studio__toolbar-icon" aria-hidden="true">Img</span>
                  </button>
                  <button
                    type="button"
                    class="cg-blog-editor-studio__toolbar-btn"
                    title="Insert link"
                    aria-label="Insert link"
                    (mousedown)="preventToolbarFocusLoss($event)"
                    (click)="formatLink()"
                  >
                    <span class="cg-blog-editor-studio__toolbar-icon" aria-hidden="true">Link</span>
                  </button>
                  <button
                    type="button"
                    class="cg-blog-editor-studio__toolbar-btn"
                    title="Inline code"
                    aria-label="Inline code"
                    (mousedown)="preventToolbarFocusLoss($event)"
                    (click)="formatCode()"
                  >
                    <span aria-hidden="true">&lt;&gt;</span>
                  </button>
                  <button
                    type="button"
                    class="cg-blog-editor-studio__toolbar-btn"
                    title="Quote"
                    aria-label="Quote"
                    (mousedown)="preventToolbarFocusLoss($event)"
                    (click)="insertQuote()"
                  >
                    <span aria-hidden="true">❝</span>
                  </button>
                  <button
                    type="button"
                    class="cg-blog-editor-studio__toolbar-btn"
                    title="Heading"
                    aria-label="Heading"
                    (mousedown)="preventToolbarFocusLoss($event)"
                    (click)="insertHeading()"
                  >
                    <span aria-hidden="true">H</span>
                  </button>
                </div>

                <input
                  #toolbarImageInput
                  type="file"
                  accept="image/*"
                  class="sr-only"
                  tabindex="-1"
                  aria-hidden="true"
                  (change)="onToolbarImage($event)"
                />
              </div>

              <div
                #editorBody
                class="cg-blog-editor-studio__editor-body"
                (click)="onEditorBodyClick($event)"
              >
                <div #editorHolder class="cg-blog-editor-studio__editor"></div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <aside class="cg-blog-editor-studio__sidebar cg-blog-editor-studio__sidebar--right">
        <section class="cg-blog-editor-studio__panel" id="blog-editor-section-assets">
          <h2 class="cg-blog-editor-studio__panel-title">Assets</h2>

          <div class="cg-blog-editor-studio__field">
            <span class="cg-blog-editor-studio__label">Featured image</span>
            <label
              class="cg-blog-editor-studio__dropzone"
              [class.cg-blog-editor-studio__dropzone--has-file]="featuredImagePreviewUrl()"
              (dragover)="onFeaturedDragOver($event)"
              (drop)="onFeaturedDrop($event)"
            >
              <input type="file" accept="image/*" class="sr-only" (change)="uploadFeatured($event)" />
              @if (featuredImagePreviewUrl()) {
                <img [src]="featuredImagePreviewUrl()" alt="Featured preview" class="cg-blog-editor-studio__dropzone-preview" />
              } @else {
                <span class="cg-blog-editor-studio__dropzone-icon" aria-hidden="true">🖼</span>
                <span class="cg-blog-editor-studio__dropzone-text">Click to upload or drag &amp; drop</span>
                <span class="cg-blog-editor-studio__dropzone-hint">1920×1080 recommended (JPG, PNG)</span>
              }
            </label>
          </div>

          <div class="cg-blog-editor-studio__field">
            <span class="cg-blog-editor-studio__label">Downloadable PDF guide</span>
            @if (guideFileName()) {
              <div class="cg-blog-editor-studio__file-chip">
                <span class="cg-blog-editor-studio__file-icon" aria-hidden="true">PDF</span>
                <span class="cg-blog-editor-studio__file-name">{{ guideFileName() }}</span>
                <button type="button" class="cg-blog-editor-studio__file-remove" (click)="removeGuide()" aria-label="Remove file">×</button>
              </div>
            } @else {
              <label class="cg-blog-editor-studio__file-upload">
                <input type="file" accept=".pdf,application/pdf" class="sr-only" (change)="uploadGuide($event)" />
                <span>+ Attach PDF guide</span>
              </label>
            }
          </div>
        </section>

        <section class="cg-blog-editor-studio__panel" id="blog-editor-section-faq">
          <h2 class="cg-blog-editor-studio__panel-title">FAQ (schema)</h2>
          @for (faq of faqItems; track $index; let i = $index) {
            <div class="cg-blog-editor-studio__faq-item">
              <input
                class="cg-blog-editor-studio__input"
                placeholder="Question"
                [(ngModel)]="faq.question"
                (ngModelChange)="onFieldChange()"
                [name]="'faq-q-' + i"
              />
              <textarea
                class="cg-blog-editor-studio__textarea"
                rows="2"
                placeholder="Answer"
                [(ngModel)]="faq.answer"
                (ngModelChange)="onFieldChange()"
                [name]="'faq-a-' + i"
              ></textarea>
              <button type="button" class="cg-blog-editor-studio__faq-remove" (click)="removeFaq(i)">Remove</button>
            </div>
          }
          <button type="button" class="cg-blog-editor-studio__add-faq" (click)="addFaq()">
            <span aria-hidden="true">+</span> Add question
          </button>
        </section>
      </aside>
    </div>
  }
</section>

@if (linkModalOpen()) {
  <app-admin-modal title="Insert link" (closed)="closeLinkModal()">
    <label class="cg-admin-field cg-admin-field--full">
      <span class="cg-admin-field__label">URL</span>
      <input
        class="cg-input"
        type="url"
        [(ngModel)]="linkDraft"
        name="linkDraft"
        placeholder="https://example.com"
        (keydown.enter)="$event.preventDefault(); applyLinkFromModal()"
      />
    </label>
    <div class="mt-4 flex flex-wrap justify-end gap-2">
      <app-admin-button variant="cancel" type="button" (click)="closeLinkModal()">Cancel</app-admin-button>
      <app-admin-button variant="primary" type="button" (click)="applyLinkFromModal()">Insert link</app-admin-button>
    </div>
  </app-admin-modal>
}
